import { unwrap } from '@hive/api';
import { Loading, Note, useLoad } from '@hive/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { useT } from '../../i18n';
import { LICENSE_ROUTE, licenseBanner, type LicenseStatus } from '../../lib/productLicense';
import { useSession } from '../../lib/session';

type Api = ReturnType<typeof useSession>['api'];

/**
 * Leitura antecipada da licença: começa junto com o catálogo de telas (antes de a tela abrir), para a faixa já estar
 * decidida quando o conteúdo aparece. Antes a leitura só começava com a tela aberta e a faixa, ao chegar, empurrava a
 * tela inteira ~50 px para baixo (CLS do Início, QA 5 · Q5-4).
 */
let early: { api: Api; promise: Promise<LicenseStatus | null>; settled: boolean; value: LicenseStatus | null } | null = null;

const fetchLicense = (api: Api) => unwrap(api.GET('/api/v1/settings/license')) as Promise<LicenseStatus>;

export function prefetchLicense(api: Api) {
  if (early?.api === api) return;
  const e: NonNullable<typeof early> = { api, settled: false, value: null, promise: Promise.resolve(null) };
  e.promise = fetchLicense(api).then(
    (v) => {
      e.settled = true;
      e.value = v;
      return v;
    },
    () => {
      e.settled = true;
      return null;
    },
  );
  early = e;
}

/** A leitura antecipada (uma vez só); depois disso cada leitura vai ao núcleo. */
function takeEarly(api: Api): Promise<LicenseStatus | null> | null {
  if (early?.api !== api) return null;
  const p = early.promise;
  early = null;
  return p;
}

/** Quanto a tela espera pela licença antes de abrir sem a faixa (núcleo lento não segura a console). */
const WAIT_MS = 1500;

/**
 * Faixa de licença no topo das telas: "Demo", dias restantes do teste, carência (aviso forte), só leitura e uso de
 * agentes a 90% e no limite. Lê a situação ao abrir e de novo a cada 10 minutos; erro de leitura não mostra nada.
 * A tela (children) só aparece depois da primeira leitura (ou de {@link WAIT_MS}): a faixa não entra por cima dela
 * depois de desenhada.
 */
export function LicenseBanner({ children }: { children?: ReactNode }) {
  const t = useT();
  const s = useSession();
  const where = useLocation();
  const tick = Math.floor(Date.now() / 600_000);
  // já lida antes de a tela abrir: desenha a faixa no primeiro quadro
  const [pre] = useState(() => (early?.api === s.api && early.settled ? { value: early.value } : null));
  const status = useLoad(
    () => takeEarly(s.api) ?? fetchLicense(s.api),
    [s.api, tick, where.pathname === LICENSE_ROUTE],
  );
  const [ready, setReady] = useState(!!pre);
  useEffect(() => {
    if (!status.loading) setReady(true);
  }, [status.loading]);
  useEffect(() => {
    if (ready) return;
    const id = window.setTimeout(() => setReady(true), WAIT_MS);
    return () => window.clearTimeout(id);
  }, [ready]);
  const b = licenseBanner(status.data ?? pre?.value);
  return (
    <>
      {b && (
        <div className="license-banner" role={b.tone === 'crit' ? 'alert' : 'status'} style={{ marginBottom: 12 }}>
          <Note tone={b.tone}>
            <span>{t(b.key, b.vars)}</span>
            {where.pathname !== LICENSE_ROUTE && b.key !== 'license.banner.demo' && (
              <>
                {' '}
                <Link to={LICENSE_ROUTE}>{t('license.banner.open')}</Link>
              </>
            )}
          </Note>
        </div>
      )}
      {children === undefined ? null : ready ? children : <Loading />}
    </>
  );
}
